/* eslint-disable @typescript-eslint/no-explicit-any */
import Mapper from './Mapper'
import { IShortCourseTag, IShortCourseLanding, IShortCourseCoach, IShortCourse, IShortCourseFilters, IShortCourseSearchResponse } from '@web/store/types/modules/shortCourse'
import { ICourseOffer, IOfferPosition, ILandingContent } from '@web/types/courseOffer'

class ShortCourseMapper {
  /**
   * Преобразование категории курса
   */
  static mapTag (tag: Record<string, any> | null): IShortCourseTag | null {
    if (!tag) return null

    return Mapper.converter({
      data: tag,
      rules: {},
      defaults: {
        sort: 999
      }
    }) as IShortCourseTag | null
  }

  /**
   * Преобразование landing информации
   */
  static mapLanding (landing: Record<string, any> | null): IShortCourseLanding | null {
    if (!landing) return null

    return Mapper.converter({
      data: landing,
      rules: {}
    }) as IShortCourseLanding | null
  }

  /**
   * Преобразование информации о тренере
   */
  static mapCoach (coach: Record<string, any> | null): IShortCourseCoach | null {
    if (!coach) return null

    return Mapper.converter({
      data: coach
    }) as IShortCourseCoach | null
  }

  /**
   * Преобразование позиции оффера
   */
  static mapOfferPosition (position: Record<string, any>): IOfferPosition {
    return Mapper.converter({ data: position }) as IOfferPosition
  }

  /**
   * Преобразование оффера курса.
   * null, если у курса нет оффера — тогда блок покупки нужно скрыть.
   * positions — словарь по номеру позиции ("1", "3"); ключи сохраняем как есть.
   */
  static mapOffer (offer: Record<string, any> | null): ICourseOffer | null {
    if (!offer) return null

    const positionsRaw = offer.positions || {}
    const positions: Record<string, IOfferPosition> = {}
    Object.keys(positionsRaw).forEach(key => {
      positions[key] = ShortCourseMapper.mapOfferPosition(positionsRaw[key])
    })

    return { positions }
  }

  /**
   * Преобразование контента лендинга (сейчас — только отзывы)
   */
  static mapLandingContent (landingContent: Record<string, any> | null): ILandingContent | null {
    if (!landingContent) return null

    return {
      reviews: (landingContent.reviews || []).map((review: Record<string, any>) =>
        Mapper.converter({ data: review })
      )
    } as ILandingContent
  }

  /**
   * Преобразование одного курса с сервера на клиент
   */
  static mapCourse (data: Record<string, any>): IShortCourse {
    return Mapper.converter({
      data,
      rules: {
        thumbnail: {
          rename: 'backgroundImage'
        },
        tags: {
          cb: (tags: Record<string, any>[]) =>
            tags.map(tag => ShortCourseMapper.mapTag(tag))
        },
        landing: {
          cb: (value) => ShortCourseMapper.mapLanding(value)
        },
        coach: {
          cb: (value) => ShortCourseMapper.mapCoach(value)
        },
        offer: {
          cb: (value) => ShortCourseMapper.mapOffer(value)
        },
        landingContent: {
          cb: (value) => ShortCourseMapper.mapLandingContent(value)
        }
      },
      defaults: {
        tags: []
      }
    }) as IShortCourse
  }

  /**
   * Преобразование фильтров
   */
  static mapFilters (data: Record<string, any>): IShortCourseFilters {
    return Mapper.converter({
      data,
      rules: {
        tags: {
          cb: (tags: Record<string, any>[]) =>
            tags.map(tag => ShortCourseMapper.mapTag(tag))
        }
      },
      defaults: {
        tags: []
      }
    }) as IShortCourseFilters
  }

  /**
   * Преобразование полного ответа поиска курсов
   */
  static toClient (data: Record<string, any>): IShortCourseSearchResponse {
    return {
      courses: (data.courses || []).map((course: Record<string, any>) =>
        ShortCourseMapper.mapCourse(course)
      ),
      filters: ShortCourseMapper.mapFilters(data.filters || { categories: [] })
    }
  }
}

export default ShortCourseMapper
